import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, initials, timeAgo, showToast } from "@/lib/api";

const SECTIONS = [
  { key: "dashboard", label: "Dashboard", icon: "📊", group: "Overview" },
  { key: "analytics", label: "Analytics", icon: "📈", group: "Overview", super: true },
  { key: "pending", label: "Pending Posts", icon: "⏳", group: "Moderation" },
  { key: "comments", label: "Pending Comments", icon: "💬", group: "Moderation" },
  { key: "flagged", label: "Flagged", icon: "🚩", group: "Moderation" },
  { key: "live", label: "Live Posts", icon: "✅", group: "Content" },
  { key: "rejected", label: "Rejected", icon: "🗑️", group: "Content" },
  { key: "users", label: "All Members", icon: "👥", group: "Users" },
  { key: "suspended", label: "Suspended", icon: "🔒", group: "Users" },
  { key: "cities", label: "City Suggestions", icon: "🌍", group: "Catalogue" },
  { key: "tags", label: "Tag Suggestions", icon: "🏷️", group: "Catalogue" },
  { key: "articles", label: "LANTEN Reads", icon: "📚", group: "Catalogue" },
  { key: "media", label: "Media & Featured", icon: "🎬", group: "Catalogue", super: true },
  { key: "contact", label: "Contact Inbox", icon: "📨", group: "Catalogue" },
  { key: "admins", label: "Admin Accounts", icon: "🛡️", group: "Admin Only", super: true },
  { key: "log", label: "Activity Log", icon: "📋", group: "Admin Only", super: true },
];

const StatusPill = ({ s }) => {
  const map = {
    pending: { c: "qs-p", l: "Pending" },
    live: { c: "qs-a", l: "Live" },
    rejected: { c: "qs-r", l: "Rejected" },
    flagged: { c: "qs-f", l: "Flagged" }
  };
  const x = map[s] || { c: "qs-p", l: s };
  return <span className={`q-st ${x.c}`}>{x.l}</span>;
};

// Inline error boundary so a stale-state render crash in a single section
// (e.g. data shape mismatch on Admin Accounts / Activity Log) doesn't black
// out the whole admin body. Resets when `resetKey` changes (section switch).
class SectionErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { err: null }; }
  static getDerivedStateFromError(err) { return { err }; }
  componentDidCatch(err, info) { console.error("Admin section render error:", err, info); }
  componentDidUpdate(prevProps) {
    if (prevProps.resetKey !== this.props.resetKey && this.state.err) {
      this.setState({ err: null });
    }
  }
  render() {
    if (this.state.err) {
      return (
        <div className="empty-q" data-testid="admin-section-error">
          <div className="eq-icon">⚠️</div>
          <div style={{fontWeight:700,color:"var(--at)",marginBottom:6}}>Something went wrong loading this section</div>
          <div style={{fontSize:12.5,color:"var(--ad)",marginBottom:14}}>{String(this.state.err.message || this.state.err)}</div>
          <button className="adm-tbtn gold" onClick={() => this.setState({ err: null })}>↻ Try again</button>
        </div>
      );
    }
    return this.props.children;
  }
}

export default function AdminDashboard() {
  const navigate = useNavigate();
  const [admin, setAdmin] = useState(null);
  const [section, setSection] = useState("dashboard");
  const [dashboard, setDashboard] = useState({ stats: {}, recent_activity: [], queue: [] });
  const [data, setData] = useState([]);
  const [previewItem, setPreviewItem] = useState(null);
  const [previewKind, setPreviewKind] = useState(null);
  const [note, setNote] = useState("");
  const [searchUser, setSearchUser] = useState("");
  const [newAdmin, setNewAdmin] = useState({ username: "", password: "", name: "", role: "moderator", cities: [] });
  const [newArticle, setNewArticle] = useState({ title: "", summary: "", body: "", tag: "GUIDE", cover_image: "" });
  const [articleUploadBusy, setArticleUploadBusy] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null); // article object being edited (null = closed)
  const [editArticleUploadBusy, setEditArticleUploadBusy] = useState(false);
  const [adminFormError, setAdminFormError] = useState("");
  const [creatingAdmin, setCreatingAdmin] = useState(false);
  const [allCities, setAllCities] = useState([]);
  const [editCitiesFor, setEditCitiesFor] = useState(null); // admin object being edited
  const [editCities, setEditCities] = useState([]);

  useEffect(() => { api.get("/cities").then(r => setAllCities((r.data||[]).map(c => c.name))); }, []);

  const loadAdmin = useCallback(async () => {
    try {
      const r = await api.get("/admin/me");
      setAdmin(r.data);
    } catch {
      navigate("/admin");
    }
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    const r = await api.get("/admin/dashboard");
    setDashboard(r.data);
  }, []);

  const loadSection = useCallback(async (sec) => {
    try {
      let url = null;
      if (sec === "pending") url = "/admin/posts/pending";
      else if (sec === "live") url = "/admin/posts/live";
      else if (sec === "rejected") url = "/admin/posts/rejected";
      else if (sec === "flagged") url = "/admin/posts/flagged";
      else if (sec === "comments") url = "/admin/comments/pending";
      else if (sec === "users") url = "/admin/users";
      else if (sec === "suspended") url = "/admin/users/suspended";
      else if (sec === "cities") url = "/admin/cities/pending";
      else if (sec === "tags") url = "/admin/tags/pending";
      else if (sec === "articles") url = "/admin/articles";
      else if (sec === "contact") url = "/admin/contact";
      else if (sec === "media") url = "/admin/media";
      else if (sec === "analytics") url = "/admin/analytics";
      else if (sec === "admins") url = "/admin/admins";
      else if (sec === "log") url = "/admin/activity";
      if (url) {
        const params = sec === "users" && searchUser ? { q: searchUser } : {};
        const r = await api.get(url, { params });
        setData(r.data || []);
      }
    } catch (e) {
      showToast(e?.response?.data?.detail || "Failed to load");
    }
  }, [searchUser]);

  useEffect(() => { loadAdmin(); }, [loadAdmin]);
  useEffect(() => {
    if (!admin) return;
    if (section === "dashboard") loadDashboard();
    else loadSection(section);
  }, [section, admin, loadDashboard, loadSection]);

  const goToSection = useCallback((key) => {
    // Reset data synchronously to avoid stale-shape crashes when switching
    // between sections that return objects vs arrays (e.g. analytics -> pending).
    setData([]);
    setSection(key);
  }, []);

  const refresh = () => {
    if (section === "dashboard") loadDashboard();
    else loadSection(section);
  };

  const logout = async () => {
    try { await api.post("/admin/logout"); } catch { /* ignore logout failures */ }
    localStorage.removeItem("admin_token");
    navigate("/admin");
  };

  const counts = dashboard.stats || {};

  const approvePost = async (id) => {
    await api.post(`/admin/posts/${id}/approve`, { note });
    showToast("Approved"); setPreviewItem(null); setNote(""); refresh();
  };
  const rejectPost = async (id) => {
    await api.post(`/admin/posts/${id}/reject`, { note });
    showToast("Rejected"); setPreviewItem(null); setNote(""); refresh();
  };
  const unflagPost = async (id) => {
    await api.post(`/admin/posts/${id}/unflag`);
    showToast("Cleared flags"); refresh();
  };
  const approveComment = async (id) => {
    await api.post(`/admin/comments/${id}/approve`, { note });
    showToast("Comment approved"); setPreviewItem(null); setNote(""); refresh();
  };
  const rejectComment = async (id) => {
    await api.post(`/admin/comments/${id}/reject`, { note });
    showToast("Comment rejected"); setPreviewItem(null); setNote(""); refresh();
  };
  const suspendUser = async (uid) => {
    await api.post(`/admin/users/${uid}/suspend`, { note });
    showToast("User suspended"); setNote(""); refresh();
  };
  const unsuspendUser = async (uid) => {
    await api.post(`/admin/users/${uid}/unsuspend`);
    showToast("User reinstated"); refresh();
  };

  const approveCity = async (id) => { await api.post(`/admin/cities/${id}/approve`); showToast("City approved"); refresh(); };
  const rejectCity = async (id) => { await api.post(`/admin/cities/${id}/reject`); showToast("Rejected"); refresh(); };
  const approveTag = async (id) => { await api.post(`/admin/tags/${id}/approve`); showToast("Tag approved"); refresh(); };
  const rejectTag = async (id) => { await api.post(`/admin/tags/${id}/reject`); showToast("Rejected"); refresh(); };

  const createAdmin = async () => {
    if (!newAdmin.username || !newAdmin.password || !newAdmin.name) {
      showToast("Fill all fields"); return;
    }
    try {
      const payload = {...newAdmin};
      if (payload.role === "super_admin") payload.cities = [];
      await api.post("/admin/admins", payload);
      showToast("Admin created");
      setNewAdmin({ username: "", password: "", name: "", role: "moderator", cities: [] });
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };

  const saveAdminCities = async () => {
    if (!editCitiesFor) return;
    try {
      await api.put(`/admin/admins/${editCitiesFor.admin_id}/cities`, { cities: editCities });
      showToast("City scope updated");
      setEditCitiesFor(null);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };

  const deleteAdmin = async (aid) => {
    if (!window.confirm("Delete this admin?")) return;
    try { await api.delete(`/admin/admins/${aid}`); showToast("Deleted"); refresh(); }
    catch(e){ showToast(e?.response?.data?.detail || "Failed"); }
  };

  const createArticle = async () => {
    if (!newArticle.title || !newArticle.body) { showToast("Title and body required"); return; }
    try {
      await api.post("/admin/articles", newArticle);
      showToast("Article created");
      setNewArticle({ title: "", summary: "", body: "", tag: "GUIDE", cover_image: "" });
      refresh();
    } catch(e){ showToast(e?.response?.data?.detail || "Failed"); }
  };

  const uploadArticleCover = async (file) => {
    if (!file) return;
    setArticleUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setNewArticle((prev) => ({ ...prev, cover_image: fullUrl }));
      showToast("Cover image uploaded");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Upload failed");
    } finally {
      setArticleUploadBusy(false);
    }
  };

  const deleteArticle = async (id) => {
    if (!window.confirm("Delete this article?")) return;
    await api.delete(`/admin/articles/${id}`); showToast("Deleted"); refresh();
  };

  const toggleArticleVisibility = async (a) => {
    const next = !a.is_hidden;
    try {
      await api.patch(`/admin/articles/${a.id}/visibility`, { is_hidden: next });
      showToast(next ? "Article hidden" : "Article visible");
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };

  const uploadEditArticleCover = async (file) => {
    if (!file || !editingArticle) return;
    setEditArticleUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      setEditingArticle((prev) => ({ ...prev, cover_image: fullUrl }));
      showToast("Cover image uploaded");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Upload failed");
    } finally {
      setEditArticleUploadBusy(false);
    }
  };

  const saveArticleEdit = async () => {
    if (!editingArticle) return;
    if (!editingArticle.title || !editingArticle.body) { showToast("Title and body required"); return; }
    try {
      await api.put(`/admin/articles/${editingArticle.id}`, {
        title: editingArticle.title,
        summary: editingArticle.summary || "",
        body: editingArticle.body,
        cover_image: editingArticle.cover_image || null,
        tag: editingArticle.tag || "GUIDE",
        is_hidden: !!editingArticle.is_hidden,
      });
      showToast("Article updated");
      setEditingArticle(null);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };

  const markContactRead = async (id) => {
    await api.post(`/admin/contact/${id}/read`); refresh();
  };
  const deleteContact = async (id) => {
    if (!window.confirm("Delete this message?")) return;
    await api.delete(`/admin/contact/${id}`); showToast("Deleted"); refresh();
  };

  const [newMedia, setNewMedia] = useState({ slot: "homepage_hero", image_url: "", title: "", subtitle: "", link: "", order: 0 });
  const [editingMedia, setEditingMedia] = useState(null);
  const [mediaUploadBusy, setMediaUploadBusy] = useState(false);

  const uploadMediaCover = async (file, target) => {
    if (!file) return;
    setMediaUploadBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const fullUrl = `${process.env.REACT_APP_BACKEND_URL}${r.data.url}`;
      if (target === "new") setNewMedia((prev) => ({ ...prev, image_url: fullUrl }));
      else setEditingMedia((prev) => ({ ...prev, image_url: fullUrl }));
      showToast("Image uploaded");
    } catch (e) {
      showToast(e?.response?.data?.detail || "Upload failed");
    } finally {
      setMediaUploadBusy(false);
    }
  };

  const saveMediaEdit = async () => {
    if (!editingMedia) return;
    try {
      await api.put(`/admin/media/${editingMedia.id}`, {
        image_url: editingMedia.image_url,
        title: editingMedia.title,
        subtitle: editingMedia.subtitle,
        link: editingMedia.link,
        order: editingMedia.order,
        is_active: editingMedia.is_active
      });
      showToast("Media updated");
      setEditingMedia(null);
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };
  const createMedia = async () => {
    if (!newMedia.image_url) { showToast("Image URL is required"); return; }
    try {
      await api.post("/admin/media", { ...newMedia, is_active: true });
      showToast("Media added");
      setNewMedia({ slot: newMedia.slot, image_url: "", title: "", subtitle: "", link: "", order: 0 });
      refresh();
    } catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };
  const updateMedia = async (id, patch) => {
    try { await api.put(`/admin/media/${id}`, patch); refresh(); }
    catch (e) { showToast(e?.response?.data?.detail || "Failed"); }
  };
  const deleteMedia = async (id) => {
    if (!window.confirm("Delete this media item?")) return;
    await api.delete(`/admin/media/${id}`); showToast("Deleted"); refresh();
  };

  if (!admin) return null;
  const isSuper = admin.role === "super_admin";
  const sectionTitle = SECTIONS.find(s => s.key === section)?.label || "Dashboard";

  return (
    <div className="admin-shell">
      <aside className="adm-sb">
        <div className="adm-sb-head">
          <div className="adm-logo"><strong>LANTEN</strong> <em>forum</em></div>
          <div className="adm-ver">Admin Panel</div>
          <div className="adm-rchip">⭐ {isSuper ? "Super Admin" : "Moderator"}</div>
        </div>
        <div className="adm-nav">
          {["Overview","Moderation","Content","Users","Catalogue","Admin Only"].map(g => {
            const items = SECTIONS.filter(s => s.group === g && (!s.super || isSuper));
            if (items.length === 0) return null;
            return (
              <div key={g}>
                <div className="adm-nsec">{g}</div>
                {items.map(s => {
                  let badge = null;
                  if (s.key === "pending" && counts.pending) badge = counts.pending;
                  if (s.key === "comments" && counts.pending_comments) badge = counts.pending_comments;
                  if (s.key === "flagged" && counts.flagged) badge = <span style={{}}>{counts.flagged}</span>;
                  if (s.key === "live") badge = counts.live;
                  if (s.key === "contact" && counts.unread_contacts) badge = counts.unread_contacts;
                  return (
                    <div key={s.key} data-testid={`adm-nav-${s.key}`} className={`adm-ni ${section===s.key?"on":""}`} onClick={()=>goToSection(s.key)}>
                      <span className="adm-ni-icon">{s.icon}</span>{s.label}
                      {badge !== null && badge !== undefined && badge !== 0 && (
                        <span className={`adm-badge ${s.key==="live"?"nb-g":s.key==="flagged"?"nb-gold":""}`}>{badge}</span>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="adm-foot">
          <div className="adm-ur">
            <div className="adm-uav">{initials(admin.name)}</div>
            <div>
              <div className="adm-un">{admin.name}</div>
              <div className="adm-ur2">@{admin.username}</div>
            </div>
            <button className="adm-lout" data-testid="admin-logout" onClick={logout}>Logout</button>
          </div>
        </div>
      </aside>

      <main className="adm-main">
        <div className="adm-topbar">
          <div className="adm-ptitle" data-testid="adm-title">{sectionTitle}</div>
          <div className="adm-tbar-r">
            <button className="adm-tbtn" onClick={refresh}>↻ Refresh</button>
            <button className="adm-tbtn gold" onClick={()=>navigate("/")}>← View Forum</button>
          </div>
        </div>
        <div className="adm-body">
        <SectionErrorBoundary resetKey={section}>

          {section === "analytics" && isSuper && (() => {
            const a = data || {};
            const max7 = Math.max(1, ...((a.last_7_days_visits||[]).map(d=>d.visits)));
            const expColors = { positive: "var(--green)", mixed: "var(--gold)", negative: "var(--red)" };
            return (
              <>
                <div className="a-stats a-stats-4">
                  <div className="astat as-blu"><div className="astat-icon">👥</div><div className="astat-lbl">Member Base</div><div className="astat-num" data-testid="analytics-members">{a.members||0}</div><div className="astat-sub">{a.members_suspended||0} suspended</div></div>
                  <div className="astat as-gold"><div className="astat-icon">👁</div><div className="astat-lbl">Homepage Visits</div><div className="astat-num" data-testid="analytics-visits">{a.homepage_visits||0}</div><div className="astat-sub">All-time</div></div>
                  <div className="astat as-grn"><div className="astat-icon">📝</div><div className="astat-lbl">Total Posts</div><div className="astat-num">{a.total_posts||0}</div><div className="astat-sub">{a.live_posts||0} live · {a.pending_posts||0} pending</div></div>
                  <div className="astat as-org"><div className="astat-icon">📖</div><div className="astat-lbl">Total Post Views</div><div className="astat-num">{a.total_post_views||0}</div><div className="astat-sub">Sum across live posts</div></div>
                </div>
                <div className="a-stats a-stats-4" style={{marginTop:14}}>
                  <div className="astat as-red"><div className="astat-icon">🚩</div><div className="astat-lbl">Flagged</div><div className="astat-num">{a.flagged_posts||0}</div><div className="astat-sub">Needs review</div></div>
                  <div className="astat as-grn"><div className="astat-icon">💬</div><div className="astat-lbl">Comments</div><div className="astat-num">{a.total_comments||0}</div><div className="astat-sub">{a.pending_comments||0} pending</div></div>
                  <div className="astat as-gold"><div className="astat-icon">👍</div><div className="astat-lbl">Total Likes</div><div className="astat-num">{a.total_likes||0}</div><div className="astat-sub">Across live posts</div></div>
                  <div className="astat as-blu"><div className="astat-icon">📨</div><div className="astat-lbl">Unread Contacts</div><div className="astat-num">{a.unread_contacts||0}</div><div className="astat-sub">{a.total_articles||0} articles</div></div>
                </div>

                <div style={{marginTop:22,background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:18}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--at)",marginBottom:14}}>Visits (last 7 days)</div>
                  <div style={{display:"flex",alignItems:"flex-end",gap:10,height:130}}>
                    {(a.last_7_days_visits||[]).map((d,i) => {
                      const h = Math.max(4, (d.visits/max7)*100);
                      return (
                        <div key={i} style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",gap:5}}>
                          <div style={{fontSize:11,color:"var(--ag)",fontWeight:600}}>{d.visits}</div>
                          <div style={{width:"100%",height:`${h}%`,background:"linear-gradient(180deg,#D4A843 0%,#C47E2B 100%)",borderRadius:5,minHeight:4,transition:"height .3s ease"}}/>
                          <div style={{fontSize:10,color:"var(--ad)"}}>{d.day.slice(5)}</div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="ana-2col" style={{marginTop:22}}>
                  <div style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--at)",marginBottom:12}}>Posts by City</div>
                    {(a.by_city||[]).map((c,i)=>{
                      const max = Math.max(1, ...((a.by_city||[]).map(x=>x.n)));
                      return (
                        <div key={i} style={{marginBottom:9}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                            <span style={{color:"var(--at)",fontWeight:600}}>{c._id}</span>
                            <span style={{color:"var(--ad)"}}>{c.n} posts · {c.views} views</span>
                          </div>
                          <div style={{height:6,background:"var(--ab3)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${(c.n/max)*100}%`,background:"linear-gradient(90deg,#C47E2B 0%,#D4A843 100%)"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                  <div style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:18}}>
                    <div style={{fontSize:13,fontWeight:700,color:"var(--at)",marginBottom:12}}>Posts by Topic</div>
                    {(a.by_topic||[]).slice(0,8).map((t,i)=>{
                      const max = Math.max(1, ...((a.by_topic||[]).map(x=>x.n)));
                      return (
                        <div key={i} style={{marginBottom:9}}>
                          <div style={{display:"flex",justifyContent:"space-between",fontSize:12,marginBottom:4}}>
                            <span style={{color:"var(--at)",fontWeight:600}}>{t._id}</span>
                            <span style={{color:"var(--ad)"}}>{t.n} posts</span>
                          </div>
                          <div style={{height:6,background:"var(--ab3)",borderRadius:3,overflow:"hidden"}}>
                            <div style={{height:"100%",width:`${(t.n/max)*100}%`,background:"linear-gradient(90deg,#2963A3 0%,#6050C8 100%)"}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div style={{marginTop:22,background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:18}}>
                  <div style={{fontSize:13,fontWeight:700,color:"var(--at)",marginBottom:12}}>Experience Sentiment</div>
                  <div style={{display:"flex",gap:14}}>
                    {(a.by_experience||[]).map((e,i)=>(
                      <div key={i} style={{flex:1,padding:"14px 16px",borderRadius:8,background:"var(--ab3)",borderLeft:`3px solid ${expColors[e._id]||"var(--ag)"}`}}>
                        <div style={{fontSize:11,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700}}>{e._id}</div>
                        <div style={{fontSize:26,fontWeight:800,color:"var(--at)"}}>{e.n}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            );
          })()}

          {section === "dashboard" && (
            <>
              <div className="a-stats">
                <div className="astat as-gold"><div className="astat-icon">⏳</div><div className="astat-lbl">Pending</div><div className="astat-num" data-testid="stat-pending">{counts.pending||0}</div><div className="astat-sub">Awaiting review</div></div>
                <div className="astat as-grn"><div className="astat-icon">✅</div><div className="astat-lbl">Live</div><div className="astat-num">{counts.live||0}</div><div className="astat-sub">Posts published</div></div>
                <div className="astat as-red"><div className="astat-icon">❌</div><div className="astat-lbl">Rejected</div><div className="astat-num">{counts.rejected||0}</div><div className="astat-sub">Not published</div></div>
                <div className="astat as-org"><div className="astat-icon">🚩</div><div className="astat-lbl">Flagged</div><div className="astat-num">{counts.flagged||0}</div><div className="astat-sub">Needs attention</div></div>
                <div className="astat as-blu"><div className="astat-icon">👥</div><div className="astat-lbl">Members</div><div className="astat-num">{counts.members||0}</div><div className="astat-sub">Registered</div></div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:18}}>
                <div>
                  <div className="q-hdr"><div className="q-hdr-t">Recent Activity</div></div>
                  <div style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:"4px 15px"}}>
                    {(dashboard.recent_activity||[]).length === 0 && <div className="empty-q"><div className="eq-icon">⚡</div>No activity yet</div>}
                    {(dashboard.recent_activity||[]).map((a,i)=>(
                      <div key={i} className="act-item">
                        <div className="act-dot" />
                        <div>
                          <div className="act-txt"><b>{a.actor_name}</b> {a.action.replace(/_/g," ")} <i style={{color:"var(--ad)"}}>{a.target_type}</i> {a.note ? `· ${a.note}`:""}</div>
                          <div className="act-meta">{timeAgo(a.timestamp)}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <div className="q-hdr"><div className="q-hdr-t">Review Queue</div><button className="adm-tbtn" onClick={()=>goToSection("pending")}>View All →</button></div>
                  {(dashboard.queue||[]).length === 0 ? (
                    <div className="empty-q"><div className="eq-icon">🎉</div>All caught up</div>
                  ) : (dashboard.queue||[]).map(p => (
                    <div key={p.id} className="dcard" style={{background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)"}} onClick={()=>{setPreviewItem(p); setPreviewKind("post");}}>
                      <div style={{fontSize:13,fontWeight:700,color:"var(--at)",marginBottom:4}}>{p.title}</div>
                      <div style={{fontSize:11,color:"var(--ad)"}}>{p.display_author} · {p.city} · {timeAgo(p.created_at)}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {(section === "pending" || section === "live" || section === "rejected" || section === "flagged") && (
            <>
              {data.length === 0 ? <div className="empty-q"><div className="eq-icon">🎉</div>Nothing here</div> :
              <table className="qtable">
                <thead><tr>
                  <th>Title</th><th>Author</th><th>City</th><th>Status</th><th>Created</th><th></th>
                </tr></thead>
                <tbody>
                {data.map(p => (
                  <tr key={p.id}>
                    <td><div style={{fontWeight:600,maxWidth:340}}>{p.title}</div></td>
                    <td><div className="q-uc"><div className="q-uav">{p.display_initials || "?"}</div><div><div className="q-un">{p.display_author}</div><div className="q-um">{p.real_email||""}</div></div></div></td>
                    <td>{p.city}</td>
                    <td><StatusPill s={p.status === "live" ? (p.is_flagged?"flagged":"live") : p.status} /></td>
                    <td>{timeAgo(p.created_at)}</td>
                    <td><div className="q-acts">
                      <button className="qab qab-vw" data-testid={`adm-view-${p.id}`} onClick={()=>{setPreviewItem(p); setPreviewKind("post");}}>View</button>
                      {p.status === "pending" && <>
                        <button className="qab qab-ok" data-testid={`adm-approve-${p.id}`} onClick={()=>approvePost(p.id)}>Approve</button>
                        <button className="qab qab-no" data-testid={`adm-reject-${p.id}`} onClick={()=>rejectPost(p.id)}>Reject</button>
                      </>}
                      {p.is_flagged && <button className="qab qab-fl" onClick={()=>unflagPost(p.id)}>Clear Flags</button>}
                    </div></td>
                  </tr>
                ))}
                </tbody>
              </table>}
            </>
          )}

          {section === "comments" && (
            data.length === 0 ? <div className="empty-q"><div className="eq-icon">🎉</div>No pending comments</div> :
            <table className="qtable">
              <thead><tr><th>Comment</th><th>Author</th><th>On Post</th><th>Created</th><th></th></tr></thead>
              <tbody>
                {data.map(c => (
                  <tr key={c.id}>
                    <td><div style={{maxWidth:340}}>{c.body}</div></td>
                    <td>{c.author_name}<div className="q-um">{c.author_email}</div></td>
                    <td>{c.post_title}</td>
                    <td>{timeAgo(c.created_at)}</td>
                    <td><div className="q-acts">
                      <button className="qab qab-ok" onClick={()=>approveComment(c.id)}>Approve</button>
                      <button className="qab qab-no" onClick={()=>rejectComment(c.id)}>Reject</button>
                    </div></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {section === "users" && (
            <>
              <div style={{marginBottom:14}}>
                <input data-testid="user-search" placeholder="Search by name, email, username…" value={searchUser}
                       onChange={(e)=>setSearchUser(e.target.value)}
                       onKeyDown={(e)=>{ if(e.key==="Enter") loadSection("users"); }}
                       style={{padding:"8px 12px",borderRadius:7,background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",fontSize:13,width:280}} />
              </div>
              <table className="qtable">
                <thead><tr><th>Member</th><th>Role</th><th>City</th><th>Posts</th><th>Status</th><th>Joined</th><th></th></tr></thead>
                <tbody>
                  {data.map(u => (
                    <tr key={u.user_id}>
                      <td><div className="q-uc"><div className="q-uav">{initials(u.real_name||u.name)}</div><div><div className="q-un">{u.real_name||u.name}</div><div className="q-um">{u.email}</div></div></div></td>
                      <td>{u.role || "—"}</td>
                      <td>{u.city || "—"}</td>
                      <td>{u.post_count || 0}</td>
                      <td><span className={`u-stat ${u.is_suspended?"us-sus":"us-act"}`}>{u.is_suspended?"Suspended":"Active"}</span></td>
                      <td>{timeAgo(u.created_at)}</td>
                      <td>
                        {u.is_suspended ? <button className="qab qab-ok" onClick={()=>unsuspendUser(u.user_id)}>Unsuspend</button>
                                        : <button className="qab qab-no" onClick={()=>suspendUser(u.user_id)}>Suspend</button>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}

          {section === "suspended" && (
            data.length === 0 ? <div className="empty-q"><div className="eq-icon">✓</div>No suspended members</div> :
            <table className="qtable">
              <thead><tr><th>Member</th><th>Email</th><th>Reason</th><th></th></tr></thead>
              <tbody>
                {data.map(u => (
                  <tr key={u.user_id}>
                    <td><div className="q-un">{u.real_name||u.name}</div></td>
                    <td>{u.email}</td>
                    <td>{u.suspend_reason||"—"}</td>
                    <td><button className="qab qab-ok" onClick={()=>unsuspendUser(u.user_id)}>Reinstate</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {section === "cities" && (
            data.length === 0 ? <div className="empty-q"><div className="eq-icon">🌍</div>No pending cities</div> :
            <table className="qtable">
              <thead><tr><th>City</th><th>Suggested</th><th></th></tr></thead>
              <tbody>{data.map(c => (
                <tr key={c.id}>
                  <td>{c.name}</td><td>{timeAgo(c.created_at)}</td>
                  <td><div className="q-acts">
                    <button className="qab qab-ok" onClick={()=>approveCity(c.id)}>Approve</button>
                    <button className="qab qab-no" onClick={()=>rejectCity(c.id)}>Reject</button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {section === "tags" && (
            data.length === 0 ? <div className="empty-q"><div className="eq-icon">🏷️</div>No pending tags</div> :
            <table className="qtable">
              <thead><tr><th>Tag</th><th>Reason</th><th>Suggested</th><th></th></tr></thead>
              <tbody>{data.map(t => (
                <tr key={t.id}>
                  <td>{t.name}</td><td>{t.reason||"—"}</td><td>{timeAgo(t.created_at)}</td>
                  <td><div className="q-acts">
                    <button className="qab qab-ok" onClick={()=>approveTag(t.id)}>Approve</button>
                    <button className="qab qab-no" onClick={()=>rejectTag(t.id)}>Reject</button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {section === "articles" && (
            <>
              <div className="add-admin-form" style={{gridTemplateColumns:"1fr 1fr 1fr 1fr auto"}}>
                <div><label>Title</label><input value={newArticle.title} onChange={(e)=>setNewArticle({...newArticle,title:e.target.value})} placeholder="Article title" /></div>
                <div><label>Tag</label><input value={newArticle.tag} onChange={(e)=>setNewArticle({...newArticle,tag:e.target.value})} placeholder="GUIDE/LEGAL/TIP" /></div>
                <div style={{gridColumn:"span 2"}}><label>Summary</label><input value={newArticle.summary} onChange={(e)=>setNewArticle({...newArticle,summary:e.target.value})} placeholder="One-line summary" /></div>
                <button data-testid="create-article-btn" className="adm-tbtn gold" onClick={createArticle}>+ Create</button>
                <div style={{gridColumn:"1 / span 2"}}>
                  <label>Cover Image URL</label>
                  <input data-testid="article-cover-url" value={newArticle.cover_image} onChange={(e)=>setNewArticle({...newArticle,cover_image:e.target.value})} placeholder="https://images.unsplash.com/... or paste any image URL" />
                </div>
                <div style={{gridColumn:"3 / span 2"}}>
                  <label>or Upload Cover</label>
                  <div style={{display:"flex",alignItems:"center",gap:8}}>
                    <input id="article-cover-upload" type="file" accept="image/*" style={{display:"none"}}
                           onChange={(e)=>uploadArticleCover(e.target.files?.[0])}
                           data-testid="article-cover-upload"/>
                    <label htmlFor="article-cover-upload" style={{padding:"9px 12px",background:"var(--ab3)",border:"1px dashed var(--abr)",borderRadius:7,color:"var(--ad)",fontSize:12,cursor:"pointer",flex:1,textAlign:"center"}}>
                      {articleUploadBusy ? "Uploading…" : "📷 Choose image (jpg/png/webp, ≤8MB)"}
                    </label>
                    {newArticle.cover_image && (
                      <div style={{width:42,height:42,borderRadius:7,backgroundImage:`url("${newArticle.cover_image}")`,backgroundSize:"cover",backgroundPosition:"center",border:"1px solid var(--abr)"}}/>
                    )}
                  </div>
                </div>
              </div>
              <div style={{marginTop:-6,marginBottom:14}}>
                <textarea value={newArticle.body} onChange={(e)=>setNewArticle({...newArticle,body:e.target.value})} placeholder="Article body…" style={{width:"100%",minHeight:80,padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,resize:"vertical",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}} />
              </div>
              <table className="qtable">
                <thead><tr><th>Cover</th><th>Title</th><th>Tag</th><th>Visibility</th><th>Author</th><th>Created</th><th></th></tr></thead>
                <tbody>{(Array.isArray(data) ? data : []).map(a => (
                  <tr key={a.id} style={{opacity: a.is_hidden ? 0.55 : 1}}>
                    <td>
                      {a.cover_image ? (
                        <div style={{width:48,height:36,borderRadius:5,backgroundImage:`url("${a.cover_image}")`,backgroundSize:"cover",backgroundPosition:"center",border:"1px solid var(--abr)"}}/>
                      ) : (
                        <div style={{width:48,height:36,borderRadius:5,background:"var(--ab3)",border:"1px solid var(--abr)",display:"flex",alignItems:"center",justifyContent:"center",color:"var(--ad)",fontSize:14}}>—</div>
                      )}
                    </td>
                    <td>{a.title}</td><td>{a.tag}</td>
                    <td>
                      {a.is_hidden
                        ? <span className="q-st qs-r" data-testid={`article-hidden-${a.id}`}>Hidden</span>
                        : <span className="q-st qs-a" data-testid={`article-visible-${a.id}`}>Visible</span>}
                    </td>
                    <td>{a.author}</td><td>{timeAgo(a.created_at)}</td>
                    <td>
                      <div className="q-acts">
                        <button className="qab qab-vw" data-testid={`edit-article-${a.id}`} onClick={()=>setEditingArticle({...a})}>Edit</button>
                        <button className="qab qab-fl" data-testid={`toggle-article-${a.id}`} onClick={()=>toggleArticleVisibility(a)}>{a.is_hidden ? "Show" : "Hide"}</button>
                        <button className="qab qab-no" data-testid={`delete-article-${a.id}`} onClick={()=>deleteArticle(a.id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                ))}</tbody>
              </table>
            </>
          )}

          {section === "contact" && (
            data.length === 0 ? <div className="empty-q"><div className="eq-icon">📨</div>No messages yet</div> :
            <table className="qtable">
              <thead><tr><th>From</th><th>Email</th><th>Phone</th><th>Message</th><th>Received</th><th></th></tr></thead>
              <tbody>{data.map(c => (
                <tr key={c.id} style={{opacity: c.is_read ? 0.6 : 1}}>
                  <td>
                    <div style={{display:"flex",alignItems:"center",gap:6}}>
                      {!c.is_read && <span style={{width:7,height:7,borderRadius:"50%",background:"var(--ag)",display:"inline-block"}}/>}
                      <strong>{c.name}</strong>
                    </div>
                  </td>
                  <td><a href={`mailto:${c.email}`} style={{color:"var(--ag)"}}>{c.email}</a></td>
                  <td><a href={`tel:${c.phone}`} style={{color:"var(--ad)"}}>{c.phone}</a></td>
                  <td><div style={{maxWidth:340,whiteSpace:"pre-wrap"}}>{c.message}</div></td>
                  <td>{timeAgo(c.created_at)}</td>
                  <td><div className="q-acts">
                    {!c.is_read && <button className="qab qab-ok" data-testid={`contact-read-${c.id}`} onClick={()=>markContactRead(c.id)}>Mark read</button>}
                    <button className="qab qab-no" onClick={()=>deleteContact(c.id)}>Delete</button>
                  </div></td>
                </tr>
              ))}</tbody>
            </table>
          )}

          {section === "media" && isSuper && (
            <>
              <div style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:18,marginBottom:14}}>
                <div style={{fontSize:13,fontWeight:700,marginBottom:12,color:"var(--at)"}}>Add new media</div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10}}>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Slot</label>
                    <select value={newMedia.slot} onChange={(e)=>setNewMedia({...newMedia,slot:e.target.value})}
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}>
                      <option value="homepage_hero">Homepage Hero (cityscape carousel)</option>
                      <option value="infeed_editorial">In-Feed Editorial (every 6 posts)</option>
                      <option value="reads_hero">Reads Hero (filmstrip)</option>
                      <option value="sidebar_feature">Sidebar Feature (Story of the Week)</option>
                    </select>
                  </div>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Image URL</label>
                    <input value={newMedia.image_url} onChange={(e)=>setNewMedia({...newMedia,image_url:e.target.value})} placeholder="https://images.unsplash.com/..."
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
                    <div style={{display:"flex",gap:8,marginTop:6,alignItems:"center"}}>
                      <input id="media-new-upload" type="file" accept="image/*" style={{display:"none"}}
                             onChange={(e)=>uploadMediaCover(e.target.files?.[0], "new")} />
                      <label htmlFor="media-new-upload" style={{padding:"5px 10px",background:"var(--ab3)",border:"1px dashed var(--abr)",borderRadius:6,color:"var(--ad)",fontSize:11,cursor:"pointer"}}>
                        {mediaUploadBusy ? "Uploading…" : "📷 or upload"}
                      </label>
                      {newMedia.image_url && <div style={{width:32,height:32,borderRadius:5,backgroundImage:`url("${newMedia.image_url}")`,backgroundSize:"cover",backgroundPosition:"center",border:"1px solid var(--abr)"}}/>}
                    </div>
                  </div>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Title (eyebrow / headline)</label>
                    <input value={newMedia.title} onChange={(e)=>setNewMedia({...newMedia,title:e.target.value})} placeholder="e.g. Mumbai, where every flat tells a deposit story"
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
                  </div>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Subtitle</label>
                    <input value={newMedia.subtitle} onChange={(e)=>setNewMedia({...newMedia,subtitle:e.target.value})} placeholder="One-line description"
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
                  </div>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Click target (link)</label>
                    <input value={newMedia.link} onChange={(e)=>setNewMedia({...newMedia,link:e.target.value})} placeholder="/post/abc or /reads or https://..."
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
                  </div>
                  <div><label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:5}}>Display order</label>
                    <input type="number" value={newMedia.order} onChange={(e)=>setNewMedia({...newMedia,order:Number(e.target.value)})}
                      style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
                  </div>
                </div>
                <button data-testid="create-media-btn" className="adm-tbtn gold" onClick={createMedia}>+ Add Media</button>
              </div>

              {data.length === 0 ? <div className="empty-q"><div className="eq-icon">🎬</div>No media yet</div> : (
                <>
                  {["homepage_hero","infeed_editorial","reads_hero","sidebar_feature"].map(slot => {
                    const items = data.filter(m => m.slot === slot);
                    if (items.length === 0) return null;
                    const slotLabel = {homepage_hero:"Homepage Hero",infeed_editorial:"In-Feed Editorial",reads_hero:"Reads Hero",sidebar_feature:"Sidebar Feature"}[slot];
                    return (
                      <div key={slot} style={{marginBottom:22}}>
                        <div style={{fontSize:12,fontWeight:700,color:"var(--ag)",textTransform:"uppercase",letterSpacing:2,marginBottom:10}}>{slotLabel} ({items.length})</div>
                        <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(260px,1fr))",gap:11}}>
                          {items.sort((a,b)=>(a.order||0)-(b.order||0)).map(m => (
                            <div key={m.id} style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,overflow:"hidden",opacity:m.is_active?1:0.5}}>
                              <div style={{height:130,backgroundImage:`url("${m.image_url}")`,backgroundSize:"cover",backgroundPosition:"center"}}/>
                              <div style={{padding:"10px 12px"}}>
                                <div style={{fontSize:13,fontWeight:600,color:"var(--at)",marginBottom:3,lineHeight:1.3}}>{m.title || "(no title)"}</div>
                                <div style={{fontSize:11,color:"var(--ad)",marginBottom:8,lineHeight:1.4}}>{m.subtitle || "—"}</div>
                                <div style={{fontSize:10,color:"var(--ad)",marginBottom:10,fontFamily:"'JetBrains Mono',monospace"}}>order: {m.order || 0} · {m.is_active?"active":"inactive"}</div>
                                <div className="q-acts">
                                  <button className="qab qab-vw" data-testid={`edit-media-${m.id}`} onClick={()=>setEditingMedia({...m})}>Edit</button>
                                  <button className="qab qab-vw" onClick={()=>updateMedia(m.id,{is_active:!m.is_active})}>{m.is_active?"Hide":"Show"}</button>
                                  <button className="qab qab-no" onClick={()=>deleteMedia(m.id)}>Delete</button>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </>
              )}
            </>
          )}

          {section === "admins" && isSuper && (
            <>
              <div className="add-admin-form" style={{gridTemplateColumns:"1fr 1fr 1fr 1fr auto"}}>
                <div><label>Username</label><input data-testid="new-admin-username" value={newAdmin.username} onChange={(e)=>setNewAdmin({...newAdmin,username:e.target.value})} /></div>
                <div><label>Password</label><input data-testid="new-admin-password" type="password" value={newAdmin.password} onChange={(e)=>setNewAdmin({...newAdmin,password:e.target.value})} /></div>
                <div><label>Display Name</label><input data-testid="new-admin-name" value={newAdmin.name} onChange={(e)=>setNewAdmin({...newAdmin,name:e.target.value})} /></div>
                <div><label>Role</label>
                  <select value={newAdmin.role} onChange={(e)=>setNewAdmin({...newAdmin,role:e.target.value, cities: e.target.value==="super_admin"?[]:newAdmin.cities})}>
                    <option value="moderator">Moderator</option>
                    <option value="super_admin">Super Admin</option>
                  </select>
                </div>
                <button data-testid="create-admin-btn" className="adm-tbtn gold" onClick={createAdmin} disabled={creatingAdmin}>
                  {creatingAdmin ? "Adding…" : "+ Add Admin"}
                </button>
                {adminFormError && (
                  <div data-testid="admin-form-error" style={{gridColumn:"1 / -1",padding:"10px 14px",
                       background:"rgba(196,64,64,.12)",border:"1px solid rgba(196,64,64,.35)",
                       borderRadius:8,color:"#FF8A8A",fontSize:13,fontWeight:600}}>
                    ⚠ {adminFormError}
                  </div>
                )}
                {newAdmin.role === "moderator" && (
                  <div style={{gridColumn:"1 / -1",marginTop:6}}>
                    <label style={{fontSize:9,textTransform:"uppercase",letterSpacing:1.5,color:"var(--ad)",fontWeight:700,display:"block",marginBottom:6}}>City Scope <span style={{textTransform:"none",letterSpacing:0,color:"var(--ad)",fontSize:10,fontWeight:400}}>(leave empty = all cities)</span></label>
                    <div style={{display:"flex",flexWrap:"wrap",gap:6}}>
                      {allCities.map(c => {
                        const on = newAdmin.cities.includes(c);
                        return (
                          <button type="button" key={c} data-testid={`new-admin-city-${c}`}
                            onClick={()=>setNewAdmin({...newAdmin, cities: on ? newAdmin.cities.filter(x=>x!==c) : [...newAdmin.cities,c]})}
                            style={{padding:"4px 11px",borderRadius:100,border:`1px solid ${on?"var(--ag)":"var(--abr)"}`,background:on?"rgba(212,168,67,.15)":"transparent",color:on?"var(--ag)":"var(--ad)",fontSize:11.5,fontWeight:600,cursor:"pointer"}}>
                            {c}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
              <table className="qtable">
                <thead><tr><th>Name</th><th>Username</th><th>Role</th><th>City Scope</th><th>Created</th><th></th></tr></thead>
                <tbody>{(Array.isArray(data) ? data : []).map(a => {
                  if (!a || !a.admin_id) return null;
                  const cities = a.cities || [];
                  const role = a.role || "moderator";
                  const scopeLabel = role === "super_admin" ? "All (super)" : (cities.length ? cities.join(", ") : "All cities");
                  return (
                    <tr key={a.admin_id}>
                      <td>{a.name || "—"}</td>
                      <td>@{a.username || "—"}</td>
                      <td>{role.replace("_"," ")}</td>
                      <td>
                        <span style={{fontSize:11.5,color:cities.length?"var(--ag)":"var(--ad)"}}>{scopeLabel}</span>
                      </td>
                      <td>{timeAgo(a.created_at)}</td>
                      <td>
                        <div className="q-acts">
                          {role === "moderator" && (
                            <button className="qab qab-vw" data-testid={`edit-cities-${a.admin_id}`} onClick={()=>{ setEditCitiesFor(a); setEditCities([...(a.cities||[])]); }}>Edit cities</button>
                          )}
                          {admin && a.admin_id !== admin.admin_id && <button className="qab qab-no" onClick={()=>deleteAdmin(a.admin_id)}>Delete</button>}
                        </div>
                      </td>
                    </tr>
                  );
                })}</tbody>
              </table>
            </>
          )}

          {section === "log" && (
            <div style={{background:"var(--ab2)",border:"1px solid var(--abr)",borderRadius:10,padding:"4px 17px"}}>
              {(() => {
                const rows = Array.isArray(data) ? data : [];
                if (rows.length === 0) return <div className="empty-q"><div className="eq-icon">📋</div>No activity</div>;
                return rows.map((a,i) => {
                  if (!a) return null;
                  const action = (a.action || "").replace(/_/g," ");
                  let when = "";
                  try { when = a.timestamp ? new Date(a.timestamp).toLocaleString() : ""; } catch (_e) { when = String(a.timestamp || ""); }
                  return (
                    <div key={a._id || a.id || i} className="act-item">
                      <div className="act-dot" />
                      <div>
                        <div className="act-txt"><b>{a.actor_name || "—"}</b> ({a.actor_role || "—"}) {action} <i style={{color:"var(--ad)"}}>{a.target_type || ""}</i> {a.note ? `· ${a.note}`:""}</div>
                        <div className="act-meta">{when}</div>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}

        </SectionErrorBoundary>
        </div>
      </main>

      {/* Edit Article Modal */}
      {editingArticle && (
        <div className="amodal-ov open" onClick={()=>setEditingArticle(null)}>
          <div className="amodal-box" onClick={(e)=>e.stopPropagation()} data-testid="edit-article-modal">
            <button className="amodal-close" onClick={()=>setEditingArticle(null)}>✕</button>
            <div style={{fontSize:18,fontWeight:700,marginBottom:14}}>Edit article</div>

            <div className="apf-label">Title</div>
            <input data-testid="edit-article-title" value={editingArticle.title || ""}
              onChange={(e)=>setEditingArticle({...editingArticle, title: e.target.value})}
              style={{width:"100%",padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,fontSize:13,outline:"none",marginBottom:6}}/>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12,marginTop:6}}>
              <div>
                <div className="apf-label">Tag</div>
                <input value={editingArticle.tag || ""}
                  onChange={(e)=>setEditingArticle({...editingArticle, tag: e.target.value})}
                  placeholder="GUIDE/LEGAL/TIP"
                  style={{width:"100%",padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,fontSize:13,outline:"none"}}/>
              </div>
              <div>
                <div className="apf-label">Visibility</div>
                <button type="button" data-testid="edit-article-visibility"
                  onClick={()=>setEditingArticle({...editingArticle, is_hidden: !editingArticle.is_hidden})}
                  className={`q-st ${editingArticle.is_hidden ? "qs-r" : "qs-a"}`}
                  style={{cursor:"pointer",border:"none",fontFamily:"inherit",padding:"7px 14px",fontSize:12,fontWeight:600}}>
                  {editingArticle.is_hidden ? "🔒 Hidden — click to show" : "👁 Visible — click to hide"}
                </button>
              </div>
            </div>

            <div className="apf-label">Summary</div>
            <input value={editingArticle.summary || ""}
              onChange={(e)=>setEditingArticle({...editingArticle, summary: e.target.value})}
              style={{width:"100%",padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,fontSize:13,outline:"none",marginBottom:6}}/>

            <div className="apf-label">Cover Image URL</div>
            <input data-testid="edit-article-cover-url" value={editingArticle.cover_image || ""}
              onChange={(e)=>setEditingArticle({...editingArticle, cover_image: e.target.value})}
              placeholder="https://..."
              style={{width:"100%",padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,fontSize:13,outline:"none"}}/>
            <div style={{display:"flex",alignItems:"center",gap:10,marginTop:8}}>
              <input id="edit-article-cover-upload" type="file" accept="image/*" style={{display:"none"}}
                     onChange={(e)=>uploadEditArticleCover(e.target.files?.[0])}
                     data-testid="edit-article-cover-upload"/>
              <label htmlFor="edit-article-cover-upload"
                style={{padding:"8px 12px",background:"var(--ab3)",border:"1px dashed var(--abr)",borderRadius:7,color:"var(--ad)",fontSize:12,cursor:"pointer",flex:1,textAlign:"center"}}>
                {editArticleUploadBusy ? "Uploading…" : "📷 Replace cover (jpg/png/webp, ≤8MB)"}
              </label>
              {editingArticle.cover_image && (
                <div style={{width:60,height:42,borderRadius:6,backgroundImage:`url("${editingArticle.cover_image}")`,backgroundSize:"cover",backgroundPosition:"center",border:"1px solid var(--abr)"}}/>
              )}
            </div>

            <div className="apf-label">Body</div>
            <textarea data-testid="edit-article-body" value={editingArticle.body || ""}
              onChange={(e)=>setEditingArticle({...editingArticle, body: e.target.value})}
              style={{width:"100%",minHeight:160,padding:"10px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",color:"var(--at)",borderRadius:7,resize:"vertical",fontFamily:"'Outfit',sans-serif",fontSize:13,outline:"none"}}/>

            <div className="amod-acts">
              <button className="qab qab-ok" data-testid="edit-article-save" onClick={saveArticleEdit}>Save changes</button>
              <button className="qab qab-vw" onClick={()=>setEditingArticle(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Preview Modal */}
      {previewItem && previewKind === "post" && (        <div className="amodal-ov open" onClick={()=>{setPreviewItem(null); setNote("");}}>
          <div className="amodal-box" onClick={(e)=>e.stopPropagation()}>
            <button className="amodal-close" onClick={()=>{setPreviewItem(null); setNote("");}}>✕</button>
            <div style={{fontSize:18,fontWeight:700,marginBottom:6}}>{previewItem.title}</div>
            <div style={{fontSize:12,color:"var(--ad)",marginBottom:14}}>by {previewItem.display_author} ({previewItem.real_email || "system"}) · {previewItem.city} · {timeAgo(previewItem.created_at)}</div>
            <div className="apf-label">Story</div>
            <div className="apf-val">{previewItem.body}</div>
            {(previewItem.tags||[]).length > 0 && <><div className="apf-label">Tags</div><div className="apf-val">{previewItem.tags.join(", ")}</div></>}
            <textarea className="amod-note" placeholder="Moderation note (optional)…" value={note} onChange={(e)=>setNote(e.target.value)} />
            {previewItem.status === "pending" && (
              <div className="amod-acts">
                <button className="qab qab-ok" data-testid="modal-approve" onClick={()=>approvePost(previewItem.id)}>Approve →</button>
                <button className="qab qab-no" data-testid="modal-reject" onClick={()=>rejectPost(previewItem.id)}>Reject</button>
              </div>
            )}
          </div>
        </div>
      )}
      {/* Edit Media modal */}
      {editingMedia && (
        <div className="amodal-ov open" onClick={()=>setEditingMedia(null)}>
          <div className="amodal-box" style={{maxWidth:580}} onClick={(e)=>e.stopPropagation()}>
            <button className="amodal-close" onClick={()=>setEditingMedia(null)}>✕</button>
            <div style={{fontSize:18,fontWeight:700,marginBottom:6}}>Edit media · {editingMedia.slot.replace("_"," ")}</div>
            <div style={{fontSize:12,color:"var(--ad)",marginBottom:16}}>Update any field. Changes go live immediately on save.</div>

            {editingMedia.image_url && (
              <div style={{height:160,borderRadius:9,backgroundImage:`url("${editingMedia.image_url}")`,backgroundSize:"cover",backgroundPosition:"center",border:"1px solid var(--abr)",marginBottom:14}}/>
            )}

            <div className="apf-label">Image URL</div>
            <input value={editingMedia.image_url || ""} onChange={(e)=>setEditingMedia({...editingMedia, image_url:e.target.value})}
                   style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none",marginBottom:6}}/>
            <div style={{display:"flex",gap:8,alignItems:"center",marginBottom:10}}>
              <input id="media-edit-upload" type="file" accept="image/*" style={{display:"none"}}
                     onChange={(e)=>uploadMediaCover(e.target.files?.[0], "edit")} />
              <label htmlFor="media-edit-upload" style={{padding:"7px 12px",background:"var(--ab3)",border:"1px dashed var(--abr)",borderRadius:7,color:"var(--ad)",fontSize:12,cursor:"pointer"}}>
                {mediaUploadBusy ? "Uploading…" : "📷 Replace with upload"}
              </label>
            </div>

            <div className="apf-label">Title (eyebrow / headline)</div>
            <input value={editingMedia.title || ""} onChange={(e)=>setEditingMedia({...editingMedia, title:e.target.value})}
                   style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none",marginBottom:10}}/>

            <div className="apf-label">Subtitle</div>
            <input value={editingMedia.subtitle || ""} onChange={(e)=>setEditingMedia({...editingMedia, subtitle:e.target.value})}
                   style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none",marginBottom:10}}/>

            <div className="apf-label">Click target (link)</div>
            <input value={editingMedia.link || ""} onChange={(e)=>setEditingMedia({...editingMedia, link:e.target.value})}
                   placeholder="/?city=Mumbai or /reads or https://..."
                   style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none",marginBottom:10}}/>

            <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:14}}>
              <div>
                <div className="apf-label">Display order</div>
                <input type="number" value={editingMedia.order || 0} onChange={(e)=>setEditingMedia({...editingMedia, order:Number(e.target.value)})}
                       style={{width:"100%",padding:"9px 12px",background:"var(--ab3)",border:"1px solid var(--abr)",borderRadius:7,color:"var(--at)",fontSize:13,outline:"none"}}/>
              </div>
              <div>
                <div className="apf-label">Visibility</div>
                <button type="button" onClick={()=>setEditingMedia({...editingMedia, is_active: !editingMedia.is_active})}
                        style={{padding:"9px 14px",borderRadius:7,border:`1px solid ${editingMedia.is_active?"var(--green)":"var(--red)"}`,
                                background:editingMedia.is_active?"rgba(42,122,80,.12)":"rgba(196,64,64,.12)",
                                color:editingMedia.is_active?"var(--green)":"var(--red)",
                                fontSize:13,fontWeight:600,cursor:"pointer",width:"100%"}}>
                  {editingMedia.is_active ? "● Active (visible)" : "○ Hidden"}
                </button>
              </div>
            </div>

            <div className="amod-acts">
              <button className="qab qab-ok" data-testid="save-media-btn" onClick={saveMediaEdit}>Save changes</button>
              <button className="qab qab-vw" onClick={()=>setEditingMedia(null)}>Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Edit cities modal */}
      {editCitiesFor && (
        <div className="amodal-ov open" onClick={()=>setEditCitiesFor(null)}>
          <div className="amodal-box" style={{maxWidth:520}} onClick={(e)=>e.stopPropagation()}>
            <button className="amodal-close" onClick={()=>setEditCitiesFor(null)}>✕</button>
            <div style={{fontSize:18,fontWeight:700,marginBottom:6}}>City scope · {editCitiesFor.name}</div>
            <div style={{fontSize:12,color:"var(--ad)",marginBottom:16}}>Pick cities this moderator can act on. Leave all unchecked to grant access to every city.</div>
            <div style={{display:"flex",flexWrap:"wrap",gap:7,marginBottom:18}}>
              {allCities.map(c => {
                const on = editCities.includes(c);
                return (
                  <button type="button" key={c} data-testid={`edit-city-${c}`}
                    onClick={()=>setEditCities(on ? editCities.filter(x=>x!==c) : [...editCities,c])}
                    style={{padding:"5px 12px",borderRadius:100,border:`1px solid ${on?"var(--ag)":"var(--abr)"}`,background:on?"rgba(212,168,67,.15)":"transparent",color:on?"var(--ag)":"var(--ad)",fontSize:12,fontWeight:600,cursor:"pointer"}}>
                    {c}
                  </button>
                );
              })}
            </div>
            <div className="amod-acts">
              <button className="qab qab-ok" data-testid="save-cities-btn" onClick={saveAdminCities}>Save scope</button>
              <button className="qab qab-vw" onClick={()=>setEditCities([])}>Reset (all cities)</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
